import React, { useState, useEffect } from 'react';
import { Select, Spin } from 'antd';
import { packageGroupService } from '../services/packageGroupService';
import { PackageGroupVO } from '../types';

const { Option } = Select;

interface PackageGroupSelectorProps {
  value?: number | number[];
  onChange?: (value: number | number[]) => void;
  placeholder?: string;
  allowClear?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
  mode?: 'multiple' | undefined;
  groupType?: 'PAY' | 'FREE';  // 按套餐组类型筛选
}

// 套餐组类型映射
const groupTypeMap: Record<string, string> = {
  'PAY': '充值套餐',
  'FREE': '免费套餐',
};

/**
 * 套餐组选择器组件
 * 从后端获取所有套餐组列表供选择
 * 支持单选和多选模式
 */
const PackageGroupSelector: React.FC<PackageGroupSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择套餐组',
  allowClear = true,
  style,
  disabled = false,
  mode,
  groupType,
}) => {
  const [loading, setLoading] = useState(false);
  const [packageGroups, setPackageGroups] = useState<PackageGroupVO[]>([]);

  // 加载套餐组列表
  useEffect(() => {
    loadPackageGroups();
  }, [groupType]);

  const loadPackageGroups = async () => {
    try {
      setLoading(true);
      const queryParams: { groupType?: string; size?: number } = {
        size: 1000, // 加载所有套餐组
      };
      if (groupType) {
        queryParams.groupType = groupType;
      }
      const response = await packageGroupService.queryPackageGroups(queryParams);
      if (response && response.content) {
        // 过滤掉已禁用的套餐组（status !== 1）
        const activeGroups = response.content.filter(group => group.status === 1);
        setPackageGroups(activeGroups);
      } else {
        setPackageGroups([]);
      }
    } catch (error) {
      console.error('加载套餐组列表失败:', error);
      setPackageGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (selectedValue: number | number[]) => {
    if (onChange) {
      onChange(selectedValue);
    }
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      allowClear={allowClear}
      style={{ width: 250, ...style }}
      disabled={disabled}
      loading={loading}
      mode={mode}
      showSearch
      optionFilterProp="label"
      optionLabelProp="label"
      filterOption={(input, option) =>
        (option?.label as string)
          ?.toLowerCase()
          ?.includes(input.toLowerCase())
      }
      notFoundContent={loading ? <Spin size="small" /> : '暂无数据'}
    >
      {packageGroups.map(group => (
        <Option 
          key={group.id} 
          value={group.id} 
          label={group.groupName}
        >
          <div>
            <div>{group.groupName}</div>
            <div style={{ fontSize: 12, color: '#999' }}>
              <span style={{ color: group.groupType === 'PAY' ? '#1890ff' : '#52c41a' }}>
                {groupTypeMap[group.groupType] || group.groupType || '未知类型'}
              </span>
              {' | '}
              {group.deviceTypeName || '未知设备类型'}
            </div>
          </div>
        </Option>
      ))}
    </Select>
  );
};

export default PackageGroupSelector;
